// client/src/v2/origem.ts
// DE ONDE A PESSOA VEIO — a primeira visita, guardada até o cadastro.
//
// Até 02/10/2026 o Forjamente não sabia de onde vinha ninguém: 46 contas
// reais, nenhuma com origem. Sem isso não dá para saber se um post, um story
// ou a landing trouxe alguém — e o lançamento é exatamente esse teste.
//
// O que se guarda é a PRIMEIRA visita deste aparelho (first touch): os
// utm_source / utm_medium / utm_campaign da URL, o referrer, a página de
// entrada e quando foi. Fica em localStorage porque entre chegar e se
// cadastrar pode passar dias, outra aba, o convite /r/CODIGO no meio. Vai no
// corpo do POST /api/auth/signup e o servidor grava em `profiles`.
//
// A LANDING (assine.forjamente.com.br) é outro domínio, com localStorage
// próprio: ela repassa os utm_ que recebeu no link do cadastro e manda o
// referrer da primeira visita dela em `origem_ref` — senão todo mundo que
// passasse por ela chegaria aqui com referrer "assine.forjamente.com.br".
// (Não é `ref`: `?ref=` já é o código de indicação.)
//
// Nada daqui decide nada no app. É só registro, e falhar em registrar nunca
// pode atrapalhar quem está entrando — por isso tudo engole erro.

export const CHAVE_ORIGEM = 'forjamente.origem';
// O CANAL do link (31/10/2026): `?origem=instagram`, posto pelo botão da
// página /ig. Diferente da primeira visita, aqui vale o ÚLTIMO link: foi ele
// que trouxe a pessoa ao cadastro. Fica também na memória desta página,
// porque o navegador interno do Instagram pode não guardar o localStorage.
const CHAVE_CANAL = 'forjamente.origem.canal';
let canalDaEntrada: Origem | undefined;

export type Origem = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  pagina?: string;
  canal?: string;
  em?: string;
};

const limpar = (v: any, max: number): string | undefined => {
  // eslint-disable-next-line no-control-regex
  const s = String(v == null ? '' : v).replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, max);
  return s || undefined;
};

/** Guarda a origem da primeira visita, se ainda não houver uma. */
export function capturarOrigem(): void {
  if (typeof window === 'undefined') return;
  try {
    const qp = new URLSearchParams(window.location.search);
    const canal = limpar(qp.get('origem'), 30)?.toLowerCase();
    if (canal && /^[a-z0-9_-]{2,30}$/.test(canal)) {
      canalDaEntrada = {
        canal,
        utm_source: limpar(qp.get('utm_source'), 100),
        utm_medium: limpar(qp.get('utm_medium'), 100),
        utm_campaign: limpar(qp.get('utm_campaign'), 100),
      };
      try { localStorage.setItem(CHAVE_CANAL, JSON.stringify(canalDaEntrada)); } catch { /* fica na memória */ }
    }
  } catch { /* URL estranha: segue sem canal */ }
  try {
    if (localStorage.getItem(CHAVE_ORIGEM)) return;   // primeira visita vence
    const qp = new URLSearchParams(window.location.search);
    // O referrer da casa não é origem: é a pessoa navegando dentro do app.
    let referrer = limpar(qp.get('origem_ref'), 300);
    if (!referrer && document.referrer) {
      try {
        if (new URL(document.referrer).host !== window.location.host) referrer = limpar(document.referrer, 300);
      } catch { /* referrer malformado: fica sem */ }
    }
    const o: Origem = {
      utm_source: limpar(qp.get('utm_source'), 100),
      utm_medium: limpar(qp.get('utm_medium'), 100),
      utm_campaign: limpar(qp.get('utm_campaign'), 100),
      referrer,
      pagina: limpar(window.location.pathname, 200),
      em: new Date().toISOString(),
    };
    localStorage.setItem(CHAVE_ORIGEM, JSON.stringify(o));
  } catch { /* sem localStorage (modo privado antigo): segue sem origem */ }
}

/** A origem guardada, para ir no cadastro — com o canal do link, se houver. */
export function lerOrigem(): Origem | undefined {
  let base: Origem | undefined;
  try {
    const o = JSON.parse(localStorage.getItem(CHAVE_ORIGEM) || 'null');
    base = o && typeof o === 'object' ? o : undefined;
  } catch { /* sem localStorage */ }
  let canal = canalDaEntrada;
  if (!canal) {
    try {
      const c = JSON.parse(localStorage.getItem(CHAVE_CANAL) || 'null');
      if (c && typeof c === 'object' && typeof c.canal === 'string') canal = c;
    } catch { /* sem localStorage */ }
  }
  if (!canal) return base;
  // O link que trouxe ao cadastro dá o canal e, se trouxe, os utm_.
  const doCanal = Object.fromEntries(Object.entries(canal).filter(([, v]) => v)) as Origem;
  return { ...(base || {}), ...doCanal };
}
